import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {IntegrationAdAccountList, IntegrationAdAccountRow} from "./IntegrationAdAccounts";
import {Box, Button, DialogActions, DialogContent, List, StepConnector, TextField, useMediaQuery, useTheme, Step, StepLabel, Stepper, Alert, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {CustomListItem, CustomListItemIcon, CustomListItemText} from "./RevContentIntegration";

function MediaGoIntegration(props) {
    const theme = useTheme();
    const {
        integration, resetIntegration, create, update, find, alert, getAdAccounts, removeIntegrationEvent
    } = props;

    const steps = ["Connect", 'Integrate Ad Account', 'Conversion API'];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [activeStep, setActiveStep] = useState(1);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);
    const [step1Data, setStep1Data] = useState(null);
    const [step2Data, setStep2Data] = useState(null);

    const {register, handleSubmit, control, setValue, watch, getValues, formState: {errors}} = useForm();
    const {fields, remove, append} = useFieldArray({
        control,
        name: "events"
    });

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props.history, props.location]);

    const handleOnSubmitStep1 = (data) => {
        setStep1Data(data);
        setActiveStep(2);
    };

    const handleOnIntegrateAdAccount = (data) => {
        setStep2Data(data);
    };

    const handleOnSubmitStep2 = () => {
        setActiveStep(3);
    };

    const handleOnSubmit = (data) => {
        if (integrationId > 0) {
            update({
                integrationId: integrationId,
                name: step1Data?.name,
                apiKey: step1Data?.apiKey,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
                adAccountId: step2Data?.adAccountId,
                adAccountName: step2Data?.adAccountName,
                currency: step2Data?.currency,
            });
        } else {
            create({
                name: step1Data?.name,
                apiKey: step1Data?.apiKey,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
                adAccountId: step2Data?.adAccountId,
                adAccountName: step2Data?.adAccountName,
                currency: step2Data?.currency,
            });
        }
    };

    const appendNewEvent = () => {
        append({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            remove(index); // from list
            return false;
        }
        setRemoveEventIndex({eventId, index}); // set event to remove from list and do API req.
    }, [remove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            remove(removeEventIndex?.index); // from list
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId}); // API req
        }
    }, [integrationId, remove, removeEventIndex, removeIntegrationEvent]);

    useEffect(() => {
        if (integrationId > 0) {
            find(integrationId);
        }
    }, [find, integrationId]);

    useEffect(() => {
        if (activeStep === 1 && integrationId > 0 && integration?.currentIntegration !== null) {
            if (integration?.currentIntegration?.id === integrationId) {
                setValue("name", integration?.currentIntegration?.networkName);
                for (const cred of integration?.currentIntegration?.integrationCredentials) {
                    if (cred?.keyName === "mediago_api_key") {
                        setValue("apiKey", cred?.keyValue);
                    }
                    if (cred?.keyName === "mediago_account_currency") {
                        setValue("currency", cred?.keyValue);
                    }
                }
            }
        }
    }, [activeStep, setValue, integration?.currentIntegration, integrationId]);

    // Set step 1 form data.
    useEffect(() => {
        if (activeStep === 1 && step1Data !== null) {
            setValue("name", step1Data?.name);
            setValue("apiKey", step1Data?.apiKey);
        }
    }, [activeStep, setValue, step1Data]);

    // Load MediaGo ad accounts if step 2.
    useEffect(() => {
        if (activeStep === 2 && integration?.adAccounts === null) {
            getAdAccounts({
                apiKey: step1Data?.apiKey,
                integrationId: integrationId
            });
        }
    }, [activeStep, getAdAccounts, step1Data?.apiKey, integration?.adAccounts, integrationId]);

    // If integrated then set step 2 data.
    useEffect(() => {
        if (activeStep === 2 && integration?.adAccounts !== null) {
            integration?.adAccounts.forEach((item) => {
                if (item?.integrated) {
                    setStep2Data({
                        adAccountId: item?.adAccountId,
                        adAccountName: item?.adAccountName,
                        currency: item?.currency
                    });
                }
            });
        }
    }, [activeStep, integration?.adAccounts])

    useEffect(() => {
        if (activeStep === 3 && integration?.currentIntegration !== null) {
            for (const cred of integration?.currentIntegration?.integrationCredentials) {
                if (cred?.keyName === "mediago_enable_conversion_api") {
                    if (cred?.keyValue === "true") {
                        setValue("enableConversionAPI", true);
                    } else {
                        setValue("enableConversionAPI", false);
                    }
                }
            }
            if (getValues("events")?.length === 0) {
                let events = [];
                integration?.currentIntegration?.integrationEvents?.forEach((event) => {
                    events.push({
                        eventId: event?.id,
                        name: event?.name,
                        includePayout: event?.includePayout
                    });
                });
                setValue("events", events);
            }
        }
    }, [activeStep, setValue, integration?.currentIntegration, getValues]);

    useEffect(() => {
        if (alert?.successMsg !== "") {
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alert?.successMsg, handleClose]);

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="media-go-account-integration"
        >
            <IntegrationHeader name="MediaGo" logo="/assets/images/traffic-sources/mediago-white.svg" id="media-go-account-integration"/>
            <DialogContent>
                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={activeStep - 1}
                    connector={
                        <StepConnector
                            sx={{"& .MuiStepConnector-line ": {borderColor: theme.palette.background.tableBorder}}}
                        />}
                    sx={{
                        margin: "0 auto",
                        width: "100%",
                        maxWidth: 800,
                        padding: "0px 24px 24px 24px"
                    }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 &&
                    <>
                        <p>
                            Please check our integration
                            guide <Link target="_blank" underline="hover" rel="noreferrer"
                                        href="https://skro.eu/docs/integrations/mediago-and-skro">here</Link>.
                        </p>

                        {integration?.loading?.integration && <Spinner overComponentBox={true}/>}
                        <form onSubmit={handleSubmit(handleOnSubmitStep1)} autoComplete={"off"}>
                            <Box display="flex" flexWrap={"wrap"}>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        id="name"
                                        label="Integration Name*"
                                        {...register("name", {required: true, maxLength: 255, minLength: 2})}
                                        name="name"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your integration name"
                                        fullWidth={true}
                                        defaultValue="MediaGo"
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.apiKey}
                                        variant="outlined"
                                        margin="dense"
                                        id="apiKey"
                                        label="API Key*"
                                        {...register("apiKey", {required: true, maxLength: 1024, minLength: 2})}
                                        name="apiKey"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your API Key"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </form>

                        <List>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Check the status of your campaigns, ads and sites.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Change daily budget and bidding.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Pause or resume campaigns, ads and sites.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Check delivery status of campaigns, ads and sites.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Sync cost on site level every hour.
                                </CustomListItemText>
                            </CustomListItem>
                        </List>
                    </>
                }

                {activeStep === 2 &&
                    <Box mb={2}>
                        <Box mb={2} style={{fontWeight: 600}}>
                            <Alert variant="filled" severity="info">
                                Choose one of the MediaGo ad account you want to integrate. If you have multiple
                                accounts
                                then you need to integrate them separately.
                            </Alert>
                        </Box>
                        <Box sx={{
                            fontWeight: 500,
                            fontSize: "14px",
                            paddingTop: 1,
                            paddingBottom: "4px"
                        }}>
                            MediaGo Ad Accounts
                        </Box>
                        {
                            integration?.loading?.adAccounts ? (
                                <Spinner overComponentBox={true}/>
                            ) : (
                                <IntegrationAdAccountList provider="MediaGo">
                                    {integration?.adAccounts?.map((item) => {
                                        return (
                                            <IntegrationAdAccountRow key={item?.adAccountId} name={item?.adAccountName || item?.name}
                                                accountId={item?.adAccountId} integrated={item?.adAccountId === step2Data?.adAccountId}
                                                onIntegrate={() => handleOnIntegrateAdAccount({
                                                            adAccountId: item?.adAccountId,
                                                            adAccountName: item?.adAccountName,
                                                            currency: item?.currency
                                                        })}/>
                                        );
                                    })}
                                </IntegrationAdAccountList>
                            )
                        }
                    </Box>
                }

                {activeStep === 3 &&
                    <Box sx={{position: "relative"}}>
                        {(integration?.loading?.conversionApi || integration?.loading?.integration) &&
                            <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="MediaGo" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={fields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent}
                            eventLabel="Conversion Type Value" eventPlaceholder="Enter MediaGo conversion type value"/>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {activeStep === 1 &&
                        <Box>
                            <Button onClick={handleSubmit(handleOnSubmitStep1)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmitStep2)} color="primary" variant="contained">
                                    Next
                                </Button>
                            </Box>
                        </>
                    }
                    {activeStep === 3 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(2)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    find: integrationActions.findIntegration,
    getAdAccounts: integrationActions.getMediaGoAdAccounts,
    create: integrationActions.createMediaGoIntegration,
    update: integrationActions.updateMediaGoIntegration,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(MediaGoIntegration);
